import { useRef, useCallback } from "react";
import type { PlanEvent } from "../../../../shared/types";
import { CATEGORY_BLOCK_COLORS } from "../../../../shared/types";
import { useDailyChecks } from "../../../events/hooks/use-daily-checks";
import { minutesToTime } from "../../hooks/drag-drop-helpers";

interface EventBlockProps {
  readonly event: PlanEvent;
  readonly topPx: number;
  readonly heightPx: number;
  readonly column: number;
  readonly totalColumns: number;
  readonly linkedTaskCount?: number;
  readonly isDragging?: boolean;
  readonly isResizing?: boolean;
  readonly overrideHeightPx?: number;
  readonly overrideTopPx?: number;
  readonly overrideStartMinutes?: number;
  readonly overrideEndMinutes?: number;
  readonly today: string;
  readonly now: Date;
  readonly onClick?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onLongPress?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onContextMenu?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onDragStart?: (
    event: PlanEvent,
    mouseYInColumn: number,
    clientX: number,
    clientY: number,
  ) => void;
  readonly onResizeStart?: (
    event: PlanEvent,
    clientY: number,
    edge: "top" | "bottom",
  ) => void;
}

const MAX_VISIBLE_COLS = 3;

export function EventBlock({
  event,
  topPx,
  heightPx,
  column,
  totalColumns,
  linkedTaskCount = 0,
  isDragging = false,
  isResizing = false,
  overrideHeightPx,
  overrideTopPx,
  overrideStartMinutes,
  overrideEndMinutes,
  today,
  now,
  onClick,
  onLongPress,
  onContextMenu,
  onDragStart,
  onResizeStart,
}: EventBlockProps) {
  const { getCheckedCount } = useDailyChecks();
  const rawHeight = overrideHeightPx ?? heightPx;
  const clampedHeight = Math.max(rawHeight, 8);
  const sizeLevel = clampedHeight >= 44 ? 4 : clampedHeight >= 30 ? 3 : clampedHeight >= 20 ? 2 : clampedHeight >= 14 ? 1 : 0;
  const textSize = ["text-[7px]", "text-[8px]", "text-[9px]", "text-[10px]", "text-[11px]"][sizeLevel];
  const timeSize = ["text-[6px]", "text-[7px]", "text-[8px]", "text-[9px]", "text-[10px]"][sizeLevel];
  const padding = ["px-0.5 py-0", "px-1 py-0", "px-1 py-px", "px-1.5 py-0.5", "px-2 py-1"][sizeLevel];
  const isTiny = sizeLevel <= 1;
  const resolvedTop = overrideTopPx ?? topPx;
  const colorClasses = CATEGORY_BLOCK_COLORS[event.category];

  const subtasks = event.eventSubtasks ?? [];
  const hasSubtasks = subtasks.length > 0;
  const checkedCount = hasSubtasks ? getCheckedCount(event.title, subtasks) : 0;
  const allDone = hasSubtasks && checkedCount === subtasks.length;

  const isPast = event.date < today
    || (event.date === today
      && event.endTime <= `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);

  const effectiveCol = column % MAX_VISIBLE_COLS;
  const effectiveTotal = Math.min(totalColumns, MAX_VISIBLE_COLS);
  const colWidthPct = 100 / effectiveTotal;
  const leftPct = effectiveCol * colWidthPct;

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const mouseStartRef = useRef({ x: 0, y: 0 });

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (e.button !== 0) return;
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const resizeZone = rect.height < 40 ? 4 : 8;
      if (rect.bottom - e.clientY <= resizeZone) return;
      if (e.clientY - rect.top <= resizeZone) return;
      e.stopPropagation();

      longPressTriggeredRef.current = false;
      mouseStartRef.current = { x: e.clientX, y: e.clientY };

      const colEl = e.currentTarget.closest("[data-daycol]");
      const colRect = colEl?.getBoundingClientRect();
      const btnRect = e.currentTarget.getBoundingClientRect();
      longPressTimerRef.current = setTimeout(() => {
        if (onLongPress) {
          longPressTriggeredRef.current = true;
          onLongPress(event, new DOMRect(
            colRect?.left ?? btnRect.left,
            btnRect.top,
            colRect?.width ?? btnRect.width,
            btnRect.height,
          ));
        }
      }, 500);

      const mouseYInColumn =
        e.clientY -
        (e.currentTarget.closest("[data-daycol]")?.getBoundingClientRect()
          .top ?? 0);
      onDragStart?.(event, mouseYInColumn, e.clientX, e.clientY);
    },
    [event, onDragStart, onLongPress],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!longPressTimerRef.current) return;
      const dx = Math.abs(e.clientX - mouseStartRef.current.x);
      const dy = Math.abs(e.clientY - mouseStartRef.current.y);
      if (dx > 5 || dy > 5) clearLongPress();
    },
    [clearLongPress],
  );

  return (
    <button
      type="button"
      data-testid={`event-block-${event.id}`}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.stopPropagation();
        if (!isDragging && !isResizing && !longPressTriggeredRef.current) {
          const colEl = e.currentTarget.closest("[data-daycol]");
          const colRect = colEl?.getBoundingClientRect();
          const btnRect = e.currentTarget.getBoundingClientRect();
          onClick?.(event, new DOMRect(
            colRect?.left ?? btnRect.left,
            btnRect.top,
            colRect?.width ?? btnRect.width,
            btnRect.height,
          ));
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.stopPropagation();
        onContextMenu?.(event, new DOMRect(e.clientX, e.clientY, 0, 0));
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={clearLongPress}
      onMouseMove={handleMouseMove}
      onMouseLeave={clearLongPress}
      className={`absolute rounded-md border-l-[3px] overflow-hidden
        ${padding} text-left select-none
        hover:brightness-105 hover:shadow-md hover:z-50
        active:brightness-95 active:shadow-md
        focus:outline-none focus:ring-1 focus:ring-accent/50
        transition-all duration-150
        ${allDone ? "opacity-50 grayscale border-dashed" : colorClasses}
        ${isPast && !allDone ? "opacity-40" : ""}
        ${isDragging ? "opacity-30 cursor-grabbing pointer-events-none" : "cursor-grab"}
        ${isResizing ? "cursor-ns-resize" : ""}`}
      style={{
        top: `${resolvedTop + 1}px`,
        height: `${clampedHeight - 2}px`,
        left: `calc(${leftPct}% + 1px)`,
        width: `calc(${colWidthPct}% - 2px)`,
        zIndex: isDragging ? 1 : totalColumns - column,
      }}
    >
      {/* Top resize handle */}
      <div
        className="absolute top-0 left-0 right-0 hidden md:flex h-2.5 cursor-ns-resize items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
        onMouseDown={(e) => {
          if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
          e.stopPropagation();
          e.preventDefault();
          onResizeStart?.(event, e.clientY, "top");
        }}
      >
        <div className="w-10 h-0.5 rounded-full bg-current opacity-30" />
      </div>

      {clampedHeight >= 44 ? (
        <>
          <span className={`block truncate font-medium ${textSize} leading-tight pr-3`}>
            {allDone ? "✦ Pause" : event.title}
          </span>
          <span className={`block truncate ${timeSize} opacity-60 leading-tight mt-0.5`}>
            {overrideStartMinutes !== undefined
              ? minutesToTime(overrideStartMinutes)
              : event.startTime}
            –
            {overrideEndMinutes !== undefined
              ? (overrideEndMinutes === 1440 ? "00:00" : minutesToTime(overrideEndMinutes))
              : (event.endTime === "24:00" ? "00:00" : event.endTime)}
          </span>
        </>
      ) : (
        <span className={`block truncate font-medium ${textSize} leading-none pr-3`}>
          <span className={`font-normal opacity-60 mr-1 ${isTiny ? timeSize : ""}`}>
            {overrideStartMinutes !== undefined
              ? minutesToTime(overrideStartMinutes)
              : event.startTime}
            –
            {overrideEndMinutes !== undefined
              ? (overrideEndMinutes === 1440 ? "00:00" : minutesToTime(overrideEndMinutes))
              : (event.endTime === "24:00" ? "00:00" : event.endTime)}
          </span>
          {allDone ? "✦ Pause" : event.title}
        </span>
      )}
      {event.recurrence && (
        <span className="absolute top-1 right-1 text-[9px] opacity-40 leading-none">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </span>
      )}
      {hasSubtasks && clampedHeight >= 32 && (
        <span className="absolute bottom-1 right-1 text-[9px] opacity-70 font-mono leading-none bg-surface/50 px-1 rounded">
          {checkedCount}/{subtasks.length}
        </span>
      )}
      {linkedTaskCount > 0 && clampedHeight >= 32 && (
        <span className="absolute bottom-1 left-1 text-[9px] opacity-65 leading-none flex items-center gap-px bg-surface/50 px-1 rounded" title={`${linkedTaskCount} tâche(s) liée(s)`}>
          <svg className="w-2.5 h-2.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="2" width="12" height="12" rx="2" />
            <path d="M5 8l2 2 4-4" />
          </svg>
          {linkedTaskCount}
        </span>
      )}

      {/* Bouton de sélection pour les petits blocs — empêche le resize */}
      {clampedHeight < 40 && (
        <div
          className="absolute right-0.5 top-1/2 -translate-y-1/2 hidden md:flex opacity-0 hover:!opacity-100 group-hover:opacity-70 transition-opacity z-10"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            const colEl = e.currentTarget.closest("[data-daycol]");
            const colRect = colEl?.getBoundingClientRect();
            const btnEl = e.currentTarget.closest("button");
            const btnRect = btnEl?.getBoundingClientRect();
            if (btnRect) {
              onClick?.(event, new DOMRect(
                colRect?.left ?? btnRect.left,
                btnRect.top,
                colRect?.width ?? btnRect.width,
                btnRect.height,
              ));
            }
          }}
        >
          <span className="w-5 h-5 flex items-center justify-center rounded bg-surface/80 shadow-sm cursor-pointer hover:bg-accent hover:text-white transition-colors" title="Ouvrir l'événement">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          </span>
        </div>
      )}

      {/* Bottom resize handle */}
      <div
        className="absolute bottom-0 left-0 right-0 hidden md:flex h-2.5 cursor-ns-resize items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
        onMouseDown={(e) => {
          if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
          e.stopPropagation();
          e.preventDefault();
          onResizeStart?.(event, e.clientY, "bottom");
        }}
      >
        <div className="w-10 h-0.5 rounded-full bg-current opacity-30" />
      </div>
    </button>
  );
}
